/**
 * NUAA Theme Controller
 * Centralized dark/light mode management for Shopify theme
 * 
 * Features:
 * - Manual theme toggle via button
 * - Image switching for dark mode variants
 * - Section color scheme swapping
 * - Dynamic content support (AJAX, cart drawer, etc.)
 * - Shopify theme editor integration
 * - localStorage persistence
 * 
 * @version 2.0.0
 */

(function () {
  'use strict';

  // ========================================
  // Configuration
  // ========================================
  const CONFIG = {
    LIGHT_CLASS: 'color-scheme-1',
    DARK_CLASS: 'color-scheme-2',
    STORAGE_KEY: 'nuaa_color_scheme',
    TRANSITION_CLASS: 'theme-swapping',
    BUTTON_ID: 'theme-toggle',
    DEBOUNCE_DELAY: 50,
  };

  // ========================================
  // State Management
  // ========================================
  const state = {
    root: document.documentElement,
    swapRAF1: null,
    swapRAF2: null,
    mutationDebounce: null,
    isInitialized: false,
  };

  // ========================================
  // Utility Functions
  // ========================================

  /**
   * Check if current theme is dark mode
   */
  function isDark() {
    return state.root.classList.contains(CONFIG.DARK_CLASS);
  }

  /**
   * Get saved theme preference from localStorage
   */
  function getSavedTheme() {
    try {
      return localStorage.getItem(CONFIG.STORAGE_KEY);
    } catch (e) {
      console.warn('[Theme Controller] localStorage not available:', e);
      return null;
    }
  }

  /**
   * Save theme preference to localStorage
   */
  function saveTheme(themeClass) {
    try {
      localStorage.setItem(CONFIG.STORAGE_KEY, themeClass);
    } catch (e) {
      console.warn('[Theme Controller] Failed to save theme:', e);
    }
  }

  // ========================================
  // Theme Application
  // ========================================

  /**
   * Swap color scheme classes on sections
   */
  function swapSections(toDark) {
    const fromClass = toDark ? CONFIG.LIGHT_CLASS : CONFIG.DARK_CLASS;
    const toClass = toDark ? CONFIG.DARK_CLASS : CONFIG.LIGHT_CLASS;

    document.querySelectorAll(`.${fromClass}`).forEach(el => {
      el.classList.remove(fromClass);
      el.classList.add(toClass);
    });
  }

  /**
   * Update all theme-aware images
   * Images with data-theme-image attribute will switch between light/dark variants
   */
  function updateImages() {
    const isDarkMode = isDark();
    const images = document.querySelectorAll('[data-theme-image]');

    images.forEach(img => {
      const lightSrcset = img.dataset.lightSrcset;
      const darkSrcset = img.dataset.darkSrcset;
      const lightSrc = img.dataset.lightSrc;
      const darkSrc = img.dataset.darkSrc;

      // Images with srcsets (responsive images)
      if (lightSrcset) {
        if (isDarkMode && darkSrcset) {
          // Validate dark image exists before switching
          const firstDarkUrl = darkSrcset.split(',')[0].trim().split(' ')[0];
          const testImage = new Image();
          testImage.onload = () => {
            img.setAttribute('srcset', darkSrcset);
            img.setAttribute('src', firstDarkUrl);
          };
          testImage.onerror = () => {
            // Dark image doesn't exist, fallback to light
            img.setAttribute('srcset', lightSrcset);
            const firstLightUrl = lightSrcset.split(',')[0].trim().split(' ')[0];
            img.setAttribute('src', firstLightUrl);
          };
          testImage.src = firstDarkUrl;
        } else {
          // Light mode or no dark variant available
          img.setAttribute('srcset', lightSrcset);
          const firstUrl = lightSrcset.split(',')[0].trim().split(' ')[0];
          img.setAttribute('src', firstUrl);
        }
        return;
      }

      // Simple images with single src (validate dark exists)
      if (lightSrc) {
        if (isDarkMode && darkSrc) {
          // Validate dark image exists before switching
          const testImage = new Image();
          testImage.onload = () => {
            img.src = darkSrc;
          };
          testImage.onerror = () => {
            // Dark image doesn't exist, fallback to light
            img.src = lightSrc;
          };
          testImage.src = darkSrc;
        } else {
          // Light mode - use light src
          if (img.src !== lightSrc) {
            img.src = lightSrc;
          }
        }
      }
    });
  }

  /**
   * Update toggle button state
   */
  function updateButton() {
    const button = document.getElementById(CONFIG.BUTTON_ID);
    if (!button) return;

    const dark = isDark();
    button.setAttribute('aria-pressed', dark ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      dark ? 'Switch to light mode' : 'Switch to dark mode'
    );

    // Update CSS color-scheme property for native browser elements
    try {
      state.root.style.colorScheme = dark ? 'dark' : 'light';
    } catch (e) {
      // Older browsers may not support this
    }
  }

  /**
   * Apply theme with smooth transition handling
   */
  function applyTheme(themeClass) {
    // Cancel any pending animation frames
    cancelAnimationFrame(state.swapRAF1);
    cancelAnimationFrame(state.swapRAF2);

    // Disable transitions during swap to prevent flash
    state.root.classList.add(CONFIG.TRANSITION_CLASS);

    // Apply the new theme
    state.root.classList.remove(CONFIG.LIGHT_CLASS, CONFIG.DARK_CLASS);
    state.root.classList.add(themeClass);

    const toDark = themeClass === CONFIG.DARK_CLASS;
    swapSections(toDark);
    updateImages();
    updateButton();

    // Re-enable transitions after paint
    state.swapRAF1 = requestAnimationFrame(() => {
      void state.root.offsetWidth; // Force reflow
      state.swapRAF2 = requestAnimationFrame(() => {
        state.root.classList.remove(CONFIG.TRANSITION_CLASS);
      });
    });
  }

  // ========================================
  // Event Handlers
  // ========================================

  /**
   * Handle toggle button click
   */
  function handleToggleClick(event) {
    const button = event.target.closest(`#${CONFIG.BUTTON_ID}`);
    if (!button) return;

    const nextTheme = isDark() ? CONFIG.LIGHT_CLASS : CONFIG.DARK_CLASS;
    saveTheme(nextTheme);
    applyTheme(nextTheme);
  }

  /**
   * Handle dynamically injected content
   * Watches for new theme-aware elements added to the DOM
   */
  function setupMutationObserver() {
    const observer = new MutationObserver((mutationList) => {
      let found = false;

      for (const mutation of mutationList) {
        if (mutation.type !== 'childList' || !mutation.addedNodes?.length) continue;

        for (const node of mutation.addedNodes) {
          if (found || !(node instanceof Element)) continue;

          // Check if new node has color scheme classes or theme-aware images
          if (
            node.matches(`.${CONFIG.LIGHT_CLASS}, .${CONFIG.DARK_CLASS}, [data-theme-image]`) ||
            node.querySelector(`.${CONFIG.LIGHT_CLASS}, .${CONFIG.DARK_CLASS}, [data-theme-image]`)
          ) {
            found = true;
            break;
          }
        }

        if (found) break;
      }

      if (found) {
        // Debounce to avoid excessive updates
        clearTimeout(state.mutationDebounce);
        state.mutationDebounce = setTimeout(() => {
          swapSections(isDark());
          updateImages();
          updateButton();
        }, CONFIG.DEBOUNCE_DELAY);
      }
    });

    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
  }

  /**
   * Handle Shopify theme editor section changes
   */
  function setupShopifyEditorSupport() {
    document.addEventListener('shopify:section:load', () => {
      const saved = getSavedTheme();
      if (saved) {
        applyTheme(saved);
      }
    });
  }

  // ========================================
  // Initialization
  // ========================================

  /**
   * Initialize theme controller on DOMContentLoaded
   */
  function init() {
    if (state.isInitialized) {
      console.warn('[Theme Controller] Already initialized');
      return;
    }

    // Theme class already applied by theme.liquid inline script
    // Just sync sections, images, and button with current state
    swapSections(isDark());
    updateImages();
    updateButton();

    // Setup event listeners
    document.addEventListener('click', handleToggleClick);
    setupMutationObserver();
    setupShopifyEditorSupport();

    state.isInitialized = true;
    
    // Expose for debugging in development
    if (window.Shopify?.designMode) {
      window.ThemeController = {
        isDark,
        applyTheme,
        updateImages,
        state: () => ({ ...state }),
      };
      console.log('[Theme Controller] Initialized (debug mode active)');
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    // DOM already loaded
    init();
  }

})();
